import { useId } from 'react';
/**
 * Local SVG storybook scene for the Journal (11 Oct 2026). An original
 * woodland picnic: soft hills, two trees, a kite, a picnic blanket and basket,
 * butterflies and daisies. No characters from books or films, no remote
 * assets, no personal data. Decorative only.
 */
export default function WoodlandArtwork({ className = '' }: { className?: string }) {
  const sky = useId(); const kite = useId();
  return <svg className={className} viewBox="0 0 560 360" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id={sky} x2="0" y2="1"><stop stopColor="#f9e8bb" /><stop offset="1" stopColor="#f6eee0" /></linearGradient>
      <linearGradient id={kite} x1="0" y1="0" x2="1" y2="1"><stop stopColor="#e9bdc3" /><stop offset="1" stopColor="#c599ac" /></linearGradient>
    </defs>
    {/* sky wash, sun and hills */}
    <path d="M44 300C-15 160 41 22 196 39C285-24 536 21 528 202L516 303Z" fill={`url(#${sky})`} />
    <circle cx="380" cy="87" r="36" fill="#ead494" opacity=".65" />
    <path d="M4 308Q142 248 285 291Q402 232 554 290L560 360H0Z" fill="#d2dfc8" />
    <path d="M0 342Q192 286 346 328T560 318V360H0Z" fill="#b9cbb0" />
    <path d="M398 356Q386 299 317 291Q219 269 244 243" stroke="#f4e9cc" strokeWidth="30" />

    {/* left tree */}
    <g stroke="#8a7658" strokeWidth="3" strokeLinecap="round"><path d="M90 287Q110 170 71 68M100 214l53-60M98 176l-45-54" /></g>
    <g fill="#b7c5a3" opacity=".85"><ellipse cx="70" cy="97" rx="51" ry="68" transform="rotate(-22 70 97)" /><ellipse cx="127" cy="147" rx="43" ry="54" transform="rotate(31 127 147)" /><ellipse cx="38" cy="160" rx="31" ry="45" /></g>
    {/* right sapling */}
    <path d="M484 311q-16-58-1-112" stroke="#8a7658" strokeWidth="3" />
    <g fill="#aec4ae"><ellipse cx="474" cy="232" rx="21" ry="8" transform="rotate(35 474 232)" /><ellipse cx="495" cy="264" rx="23" ry="8" transform="rotate(-35 495 264)" /><ellipse cx="482" cy="203" rx="18" ry="7" transform="rotate(-20 482 203)" /></g>

    {/* picnic blanket with a gingham check */}
    <g transform="translate(176 262) skewX(-22)">
      <rect width="150" height="46" rx="4" fill="#f3dfe0" stroke="#d4a9b0" strokeWidth="2" />
      {[25, 50, 75, 100, 125].map((x) => <path key={x} d={`M${x} 0v46`} stroke="#e7c3c8" strokeWidth="7" opacity=".7" />)}
      {[12, 30].map((y) => <path key={y} d={`M0 ${y}h150`} stroke="#e7c3c8" strokeWidth="7" opacity=".7" />)}
    </g>
    {/* basket */}
    <g transform="translate(232 238)">
      <path d="M6 18q32-34 64 0" stroke="#9b7849" strokeWidth="4" fill="none" />
      <path d="M0 18h76l-8 30H8Z" fill="#d9b680" stroke="#9b7849" strokeWidth="2" strokeLinejoin="round" />
      <path d="M6 28h64M10 38h56" stroke="#b8935e" strokeWidth="2" />
      <path d="M14 18q10-10 22 0" fill="#f3dfe0" stroke="#d4a9b0" strokeWidth="1.5" />
    </g>
    {/* a cup and a little plate */}
    <g transform="translate(316 282)"><ellipse cx="10" cy="10" rx="16" ry="5" fill="#fff9eb" stroke="#c9b48c" strokeWidth="1.5" /><path d="M2 0h16l-2 9H4Z" fill="#fff9eb" stroke="#c9b48c" strokeWidth="1.5" /></g>

    {/* kite on a long string, tied near the basket */}
    <path d="M270 240Q330 170 368 140T420 96" stroke="#9e8d76" strokeWidth="1.4" />
    <g transform="translate(420 96) rotate(-18)">
      <path d="M0-30L20 0 0 34-20 0Z" fill={`url(#${kite})`} stroke="#a8777a" strokeWidth="2" strokeLinejoin="round" />
      <path d="M0-30v64M-20 0h40" stroke="#fff3f0" strokeWidth="1.5" />
      <path d="M0 34q-8 14 4 22t-2 24" stroke="#a8777a" strokeWidth="1.4" />
      {[46, 60].map((y) => <path key={y} d={`m-5 ${y} 10-4`} stroke="#d7b879" strokeWidth="3" strokeLinecap="round" />)}
    </g>

    {/* butterflies */}
    {[[157, 117, '#e9bdc3'], [319, 125, '#d7b879'], [212, 70, '#c9d6e6']].map(([x, y, c]) => <g key={`${x}`} transform={`translate(${x} ${y}) rotate(-12)`}>
      <ellipse cx="-6" cy="-4" rx="7" ry="9" fill={c as string} opacity=".9" /><ellipse cx="6" cy="-4" rx="7" ry="9" fill={c as string} opacity=".9" />
      <ellipse cx="-5" cy="7" rx="5" ry="6" fill={c as string} opacity=".75" /><ellipse cx="5" cy="7" rx="5" ry="6" fill={c as string} opacity=".75" />
      <path d="M0-8v18" stroke="#746043" strokeWidth="2" strokeLinecap="round" />
    </g>)}

    {/* daisies */}
    {[[58, 317], [127, 302], [372, 318], [443, 323], [523, 308]].map(([x, y]) => <g key={x} transform={`translate(${x} ${y})`}><path d="M0 15V0" stroke="#819576" /><circle cy="-3" r="7" fill="#f5e8dd" /><circle cy="-3" r="3" fill="#d7b879" /></g>)}
  </svg>;
}
